import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout, Fld } from "@/pages/Login";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export function GoogleButton({ label = "Continue with Google" }) {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const go = () => { window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/")}`; };
  return <button type="button" className="gb" onClick={go} data-testid="google-signin-btn"><i>G</i>{label}</button>;
}

const Msg = ({ ok, children, id }) => <div className={`msg ${ok ? "ok" : ""}`} data-testid={id}>{children}</div>;
const Back = ({ to = "/login", children = "Back to sign in" }) => <p className="mu" style={{ textAlign: "center", marginTop: 16 }}><Link to={to} className="lk">{children}</Link></p>;

export function AuthCallback() {
  const { hash } = useLocation();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(hash.slice(1)).get("session_id");
    api.post("/auth/google", { session_id: sid }).then(async () => { window.history.replaceState(null, "", "/"); await refresh(); navigate("/", { replace: true }); })
      .catch((e) => setError(errMsg(e)));
  }, [hash, refresh, navigate]);
  return error ? <AuthLayout title="Google sign-in failed"><Msg id="google-error">{error}</Msg><Back /></AuthLayout> : null;
}

const strength = (v) => (v.length >= 8) + (/[A-Z]/.test(v) && /[a-z]/.test(v)) + /\d/.test(v) + /[^A-Za-z0-9]/.test(v);

export function Register() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", t: false });
  const [er, setEr] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const n = strength(f.password);
  const submit = async (e) => {
    e.preventDefault();
    const v = { n: f.name.trim().length < 2 ? "Enter your full name" : "", e: /^\S+@\S+\.\S+$/.test(f.email.trim()) ? "" : "Enter a valid email address", p: f.password.length < 8 ? "Use at least 8 characters" : "", t: f.t ? "" : "Please accept the terms to continue" };
    setEr(v); setError("");
    if (Object.values(v).some(Boolean)) return;
    setBusy(true);
    try { await api.post("/auth/register", { name: f.name.trim(), email: f.email.trim(), password: f.password }); await refresh(); navigate("/", { replace: true }); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout title="Create your account" subtitle="Start managing your company’s compliance in minutes.">
      <GoogleButton label="Sign up with Google" />
      <div className="or">or</div>
      <form onSubmit={submit} noValidate>
        <Fld id="n" label="Full name" value={f.name} onChange={set("name")} err={er.n} testId="register-name" autoComplete="name" />
        <Fld id="e" label="Email" type="email" value={f.email} onChange={set("email")} err={er.e} testId="register-email" autoComplete="email" />
        <div className="fl"><label className="lb" style={{ marginTop: 0 }} htmlFor="f-p">Password</label>
          <input className="sel" id="f-p" type="password" autoComplete="new-password" value={f.password} onChange={(e) => set("password")(e.target.value)} data-testid="register-password" />
          <div className="pwb"><i style={{ width: `${n * 25}%`, background: n <= 1 ? "var(--er)" : n === 2 ? "var(--wa)" : "var(--ok)" }} data-testid="register-password-strength" /></div>
          {er.p && <small className="er" data-testid="register-password-error">{er.p}</small>}</div>
        <label className="r" style={{ marginTop: 14, alignItems: "flex-start", fontSize: 13.5 }}><input type="checkbox" checked={f.t} onChange={(e) => set("t")(e.target.checked)} style={{ marginTop: 3 }} data-testid="register-terms" />
          <span>I agree to the Terms and Privacy Policy, and understand that V.A staff review the company documents I upload.</span></label>
        {er.t && <small className="er" data-testid="register-terms-error">{er.t}</small>}
        {error && <Msg id="register-error">{error}</Msg>}
        <button type="submit" className="b" disabled={busy} data-testid="register-submit">{busy ? "Creating…" : "Create account"}</button>
      </form>
      <p className="mu" style={{ textAlign: "center", marginTop: 16 }}>Already have an account? <Link to="/login" className="lk" data-testid="register-signin-link">Sign in</Link></p>
    </AuthLayout>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  const [error, setError] = useState("");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post("/auth/verify-email", { token: params.get("token") || "" }).then(() => setState("ok")).catch((e) => { setError(errMsg(e)); setState("error"); });
  }, [params]);
  return (
    <AuthLayout title="Email confirmation">
      {state === "checking" ? <p className="mu">Checking your link…</p> : state === "ok" ? <Msg ok id="verify-ok">Your email is confirmed.</Msg> : <Msg id="verify-error">{error}</Msg>}
      <Link to="/" className="b" data-testid="verify-continue">Continue</Link>
    </AuthLayout>
  );
}

export function ConfirmEmailChange() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking");
  const [msg, setMsg] = useState("");
  const [needPw, setNeedPw] = useState(false);
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    api.post("/auth/email-change/confirm", { token: params.get("token") || "" }).then((r) => { setNeedPw(!!r.data.password_reset_required); setMsg(r.data.password_reset_required ? "Your login email is changed. Set your own password with 'Forgot password' using the new email, then sign in." : "Your login email is changed. Please sign in again with the new email."); setState("ok"); }).catch((e) => { setMsg(errMsg(e)); setState("error"); });
  }, [params]);
  return (
    <AuthLayout title="Confirm new login email">
      {state === "checking" ? <p className="mu">Checking your link…</p> : state === "ok" ? <Msg ok id="email-change-ok">{msg}</Msg> : <Msg id="email-change-error">{msg}</Msg>}
      {needPw && <Link to="/forgot-password" className="b" data-testid="email-change-forgot">Set password</Link>} <Link to="/login" className="b" data-testid="email-change-login">Sign in</Link>
    </AuthLayout>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const submit = (e) => { e.preventDefault(); setError(""); api.post("/auth/forgot-password", { email }).then(() => setSent(true)).catch((err) => setError(errMsg(err))); };
  return (
    <AuthLayout title="Reset your password" subtitle="We'll email you a link that is valid for 1 hour.">
      {sent ? <Msg ok id="forgot-sent">If an account exists for {email}, a reset link has been sent.</Msg> : (
        <form onSubmit={submit}>
          <Fld id="e" label="Email" type="email" value={email} onChange={setEmail} testId="forgot-email" required />
          {error && <Msg id="forgot-error">{error}</Msg>}
          <button type="submit" className="b" data-testid="forgot-submit">Send reset link</button>
        </form>)}
      <Back />
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [state, setState] = useState("");
  const [error, setError] = useState("");
  const submit = (e) => { e.preventDefault(); setError(""); api.post("/auth/reset-password", { token: params.get("token") || "", password: pw }).then(() => setState("ok")).catch((err) => setError(errMsg(err))); };
  return (
    <AuthLayout title="Choose a new password">
      {state === "ok" ? <Msg ok id="reset-ok">Password updated. You can now sign in.</Msg> : (
        <form onSubmit={submit}>
          <Fld id="p" label="New password (min 8 characters)" type="password" value={pw} onChange={setPw} testId="reset-password" required minLength={8} />
          {error && <Msg id="reset-error">{error}</Msg>}
          <button type="submit" className="b" data-testid="reset-submit">Update password</button>
        </form>)}
      <Back>Sign in</Back>
    </AuthLayout>
  );
}
